'use client'

import {
  useCallback,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import Image from 'next/image'
import { useKeenSlider } from 'keen-slider/react'
import 'keen-slider/keen-slider.min.css'
import {
  keenAutoplayPlugin,
  type KeenAutoplayControl,
} from '@/lib/keen-autoplay-plugin'
import {
  keenSliderReadyClass,
  useKeenSliderReady,
} from '@/lib/keen-slider-ready'
import { normalizeCmsPlainText } from '@/lib/cms-text'
import { getStrapiMedia } from '@/lib/strapi/client'
import { getCtaConfig } from '@/lib/cta'
import {
  getMuxPlaybackId,
  isVideoFileUrl,
  toVideoEmbedUrl,
} from '@/lib/video-urls'
import { CmsCta, MuxVideo } from '@/components/ui'
import FadeUp from '@/components/ui/FadeUp'
import type {
  HeroSliderCarouselItemType,
  HeroSliderConfigType,
} from '@/lib/types/teaser'
import type { CtaType } from '@/lib/types/shared'
import HeroSliderDot from './HeroSliderDot'
import HeroSliderPlayPause from './HeroSliderPlayPause'

function subscribeReducedMotion(onChange: () => void) {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

function getReducedMotionSnapshot() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeReducedMotion,
    getReducedMotionSnapshot,
    () => false,
  )
}

const SLIDE_INTERVAL_FALLBACK_MS = 5000

function slideIntervalMs(interval: number | null | undefined): number {
  if (interval == null || !Number.isFinite(interval) || interval <= 0) {
    return SLIDE_INTERVAL_FALLBACK_MS
  }
  return interval > 100 ? interval : interval * 1000
}

function isVideoMedia(url: string | null | undefined): boolean {
  return isVideoFileUrl(url)
}

function AmbientVideo({
  src,
  label,
}: {
  src: string
  label: string
}) {
  return (
    <video
      className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      src={src}
      autoPlay
      muted
      loop
      playsInline
      aria-label={label}
    />
  )
}

function SlideBackground({
  slide,
  priority,
  showPlayPause = true,
  active = true,
}: {
  slide: HeroSliderCarouselItemType
  priority: boolean
  showPlayPause?: boolean
  active?: boolean
}) {
  const media = slide.upload
  const alt = media?.alternativeText ?? slide.heading ?? 'Hero media'
  const externalUrl =
    slide.isExternalVideo === true ? slide.videoUrl?.trim() || null : null
  const uploadUrl = media?.url ? getStrapiMedia(media.url) : null

  if (externalUrl) {
    const muxId = getMuxPlaybackId(externalUrl)
    if (muxId) {
      return (
        <MuxVideo
          playbackId={muxId}
          label={alt}
          variant="cover"
          active={active}
          showPlayPause={showPlayPause}
          className="pointer-events-none object-cover"
        />
      )
    }

    const embed = toVideoEmbedUrl(externalUrl, { autoplay: true })
    if (embed) {
      return (
        <iframe
          src={embed}
          title={alt}
          className="pointer-events-none absolute inset-0 h-full w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          tabIndex={-1}
        />
      )
    }

    return <AmbientVideo src={externalUrl} label={alt} />
  }

  if (uploadUrl) {
    const muxId =
      getMuxPlaybackId(uploadUrl) || getMuxPlaybackId(media?.url ?? null)
    if (muxId) {
      return (
        <MuxVideo
          playbackId={muxId}
          label={alt}
          variant="cover"
          active={active}
          showPlayPause={showPlayPause}
          className="pointer-events-none object-cover"
        />
      )
    }

    if (isVideoMedia(uploadUrl) || isVideoMedia(media?.name)) {
      return <AmbientVideo src={uploadUrl} label={alt} />
    }

    return (
      <Image
        src={uploadUrl}
        alt={alt}
        fill
        priority={priority}
        fetchPriority={priority ? 'high' : undefined}
        className="pointer-events-none object-cover object-center"
        sizes="100vw"
        draggable={false}
      />
    )
  }

  return <div className="absolute inset-0 bg-[#F4F1EE]" aria-hidden="true" />
}

function heroCtaSizeClass(cta: CtaType): string {
  const label = (cta.label ?? '').trim().toLowerCase()
  if (label.includes('vip')) return 'md:h-[52px] md:w-auto md:min-w-[173px]'
  if (label.includes('learn more'))
    return 'md:h-[52px] md:w-auto md:min-w-[136px]'
  return 'md:h-[52px] md:w-auto'
}

function getAlignClasses(isLayoutB: boolean) {
  // TeaserHero defaults: Layout A → Center, Layout B → Left (mobile always center).
  const desktopAlign = isLayoutB ? 'Left' : 'Center'
  if (desktopAlign === 'Left') {
    return {
      section: 'items-center md:items-start',
      text: 'items-center text-center md:items-start md:text-left',
      paragraph: 'text-center md:text-left',
      cta: 'justify-center md:justify-start',
      justifyItems: 'justify-items-center md:justify-items-start',
    }
  }
  return {
    section: 'items-center',
    text: 'items-center text-center',
    paragraph: 'text-center',
    cta: 'justify-center',
    justifyItems: 'justify-items-center',
  }
}

function slideHasCopy(slide: HeroSliderCarouselItemType | undefined): boolean {
  if (!slide || slide.addCardContent === false) return false
  return Boolean(
    slide.heading || slide.description || (slide.cta?.length ?? 0) > 0,
  )
}

function SlideCopyBody({
  slide,
  interactive,
  layout,
}: {
  slide: HeroSliderCarouselItemType | undefined
  interactive?: boolean
  layout: 'Layout A' | 'Layout B'
}) {
  if (!slideHasCopy(slide)) return null

  const isLayoutB = layout === 'Layout B'
  const { text, paragraph, cta: ctaRow } = getAlignClasses(isLayoutB)
  const heading = normalizeCmsPlainText(slide?.heading)
  const showHeading = Boolean(heading)
  const showDescription = Boolean(slide?.description)
  const ctas = slide?.cta ?? []
  const descriptionDelay = Number(showHeading) * 100
  const ctaDelay = (Number(showHeading) + Number(showDescription)) * 100

  return (
    <div
      className={
        isLayoutB
          ? `relative z-10 isolate flex w-full max-w-full flex-col gap-4 self-stretch md:max-w-[572px] md:gap-6 md:self-auto ${text}`
          : `relative z-10 flex w-full max-w-full flex-col gap-4 self-stretch md:max-w-[572px] md:gap-spacing-gap-md md:self-auto ${text}`
      }
      aria-hidden={interactive === false ? true : undefined}
    >
      {isLayoutB && (
        <div
          className="pointer-events-none absolute left-0 top-1/2 z-0 h-[184px] w-full -translate-y-1/2 bg-[#FFEABB] blur-[68.55px] md:h-[286px] md:blur-[104.55px]"
          aria-hidden="true"
        />
      )}

      {(showHeading || showDescription) && (
        <div
          className={
            isLayoutB
              ? `relative z-[1] flex w-full flex-col gap-4 ${text}`
              : 'contents'
          }
        >
          {showHeading && (
            <h1
              className={
                isLayoutB
                  ? 'relative z-[1] w-full font-heading text-[40px] font-bold leading-[46px] tracking-[-1px] text-hero-heading md:max-w-[442px] md:text-[60px] md:leading-[66px] md:tracking-[-2px]'
                  : 'w-full text-hero font-heading text-hero-heading max-md:leading-[46px] max-md:tracking-[-1px]'
              }
            >
              {heading}
            </h1>
          )}

          {showDescription && (
            <FadeUp
              className={
                isLayoutB
                  ? 'relative z-[2] flex w-full flex-col gap-1 font-body text-sm font-normal leading-[150%] text-hero-description opacity-70 md:text-[20px] md:leading-7 md:opacity-80'
                  : 'flex w-full flex-col gap-1 text-subtitle font-body text-hero-description'
              }
              delay={descriptionDelay}
            >
              <p
                className={
                  isLayoutB
                    ? `${paragraph} text-inherit`
                    : `${paragraph} text-hero-description`
                }
              >
                {slide?.description}
              </p>
            </FadeUp>
          )}
        </div>
      )}

      {ctas.length > 0 && (
        <FadeUp
          delay={ctaDelay}
          className={
            isLayoutB
              ? `pointer-events-auto relative z-[3] flex w-full flex-col gap-3 self-stretch md:flex-row md:flex-wrap md:items-start md:gap-4 ${ctaRow}`
              : `pointer-events-auto flex w-full flex-col gap-3 md:w-auto md:flex-row md:flex-wrap md:gap-4 ${ctaRow}`
          }
        >
          {ctas.map((cta, idx) => {
            const isLink = getCtaConfig(cta).displayType === 'Link'
            return (
              <CmsCta
                key={cta.id}
                cta={cta}
                showLinkArrow={isLink}
                data-fig={`hero-slider-cta-${idx}`}
                className={
                  isLink ? undefined : `h-[52px] w-full ${heroCtaSizeClass(cta)}`
                }
                layout={layout}
              />
            )
          })}
        </FadeUp>
      )}
    </div>
  )
}

function StackedSlideCopy({
  slides,
  activeIndex,
  layout,
}: {
  slides: HeroSliderCarouselItemType[]
  activeIndex: number
  layout: 'Layout A' | 'Layout B'
}) {
  const anyCopy = slides.some((s) => slideHasCopy(s))
  if (!anyCopy) return null

  const isLayoutB = layout === 'Layout B'
  const { section, justifyItems } = getAlignClasses(isLayoutB)

  return (
    <div
      className={
        isLayoutB
          ? `relative z-10 flex w-full flex-1 flex-col justify-end px-4 pb-6 pt-[158px] pointer-events-none md:justify-center md:px-spacing-section-x md:pb-spacing-section-y md:pt-[min(18vh,9rem)] ${section}`
          : `relative z-10 flex w-full flex-1 flex-col justify-end px-4 pb-6 pt-[158px] pointer-events-none md:px-spacing-section-x md:pb-spacing-section-y md:pt-0 ${section}`
      }
      aria-live="polite"
    >
      <div
        className={`grid w-full min-w-0 md:max-w-[572px] md:self-auto ${justifyItems}`}
      >
        {slides.map((slide, index) => {
          const isActive = index === activeIndex
          return (
            <div
              key={slide.id ?? index}
              className={`col-start-1 row-start-1 min-w-0 w-full ${
                isActive ? 'visible' : 'invisible'
              }`}
              aria-hidden={!isActive}
              inert={!isActive ? true : undefined}
            >
              <SlideCopyBody
                slide={slide}
                interactive={isActive}
                layout={layout}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function DarkScrim() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(180deg,rgba(0,0,0,0.35)_28.98%,rgba(0,0,0,0.7)_100%)] md:bg-[linear-gradient(180deg,rgba(0,0,0,0.2)_1.36%,rgba(0,0,0,0.4)_57.5%)]"
      aria-hidden="true"
    />
  )
}

function StaticHeroSlide({
  slide,
  layout,
}: {
  slide: HeroSliderCarouselItemType
  layout: 'Layout A' | 'Layout B'
}) {
  const isLayoutB = layout === 'Layout B'
  return (
    <div className="relative flex min-h-[439px] w-full flex-col md:h-auto md:min-h-[calc(100svh-var(--height-header))]">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <SlideBackground slide={slide} priority />
        {!isLayoutB && <DarkScrim />}
      </div>
      <StackedSlideCopy slides={[slide]} activeIndex={0} layout={layout} />
    </div>
  )
}

function CarouselHeroSlides({
  slides,
  autoplayEnabled,
  intervalMs,
  showControls,
  layout,
}: {
  slides: HeroSliderCarouselItemType[]
  autoplayEnabled: boolean
  intervalMs: number
  showControls: boolean
  layout: 'Layout A' | 'Layout B'
}) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [loaderCycle, setLoaderCycle] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isPlaying, setIsPlaying] = useState(true)
  const isPlayingRef = useRef(true)
  const autoplayControlRef = useRef<KeenAutoplayControl | null>(null)
  const advanceRef = useRef<() => void>(() => {})

  isPlayingRef.current = isPlaying

  const startLoader = useCallback(() => {
    setIsTransitioning(false)
    setLoaderCycle((c) => c + 1)
  }, [])

  const togglePlayPause = useCallback(() => {
    const next = !isPlayingRef.current
    isPlayingRef.current = next
    setIsPlaying(next)
    if (next) {
      autoplayControlRef.current?.resume()
    } else {
      autoplayControlRef.current?.pause()
    }
  }, [])

  // Dot progress ring drives advance when controls are visible; otherwise use timer.
  const useLoaderSync = autoplayEnabled && showControls

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      loop: true,
      slides: { perView: 1 },
      defaultAnimation: { duration: 800 },
      slideChanged(slider) {
        setActiveIndex(slider.track.details.rel)
        setIsTransitioning(true)
      },
    },
    autoplayEnabled
      ? [
          keenAutoplayPlugin(
            intervalMs,
            startLoader,
            () => advanceRef.current(),
            isPlayingRef,
            autoplayControlRef,
            useLoaderSync,
          ),
        ]
      : [],
  )

  advanceRef.current = () => {
    if (isPlayingRef.current) instanceRef.current?.next()
  }

  const sliderReady = useKeenSliderReady(instanceRef, slides.length)
  const dotsChromeClass = 'pt-2 pb-6'
  const dotsReserveClass = showControls ? 'pb-[53px]' : ''
  const isLayoutB = layout === 'Layout B'

  return (
    <div className="relative flex min-h-[439px] w-full flex-col md:h-auto md:min-h-[calc(100svh-var(--height-header))]">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div
          ref={sliderRef}
          className={`keen-slider keen-slider--hero h-full w-full transition-opacity duration-200 ${keenSliderReadyClass(sliderReady)}`}
        >
          {slides.map((slide, index) => {
            const isActive = activeIndex === index
            return (
              <div
                key={slide.id ?? index}
                className="keen-slider__slide relative h-full min-h-full w-full"
                aria-hidden={!isActive}
                inert={!isActive ? true : undefined}
              >
                <SlideBackground
                  slide={slide}
                  priority={index === 0}
                  showPlayPause={isActive}
                  active={isActive}
                />
                {!isLayoutB && <DarkScrim />}
              </div>
            )
          })}
        </div>
      </div>

      <div
        className={`relative z-10 flex min-h-0 flex-1 flex-col pointer-events-none ${dotsReserveClass}`}
      >
        <StackedSlideCopy
          slides={slides}
          activeIndex={activeIndex}
          layout={layout}
        />
      </div>

      {showControls && (
        <div
          className={`absolute inset-x-0 bottom-0 z-20 flex ${dotsChromeClass} items-center justify-center pointer-events-none ${
            autoplayEnabled ? 'gap-[11px] md:gap-4' : 'gap-[5.5px] md:gap-2'
          }`}
        >
          <div
            className={`flex items-center justify-center pointer-events-auto ${
              autoplayEnabled ? 'gap-[11px] md:gap-4' : 'gap-[5.5px] md:gap-2'
            }`}
            role="group"
            aria-label="Hero slides"
          >
            {slides.map((slide, index) => (
              <HeroSliderDot
                key={slide.id ?? index}
                active={activeIndex === index}
                label={`Go to slide ${index + 1}${slide.heading ? `: ${slide.heading}` : ''}`}
                onClick={() => instanceRef.current?.moveToIdx(index)}
                durationMs={intervalMs}
                showLoader={autoplayEnabled}
                loaderCycle={loaderCycle}
                isTransitioning={isTransitioning}
                isPlaying={isPlaying}
                onLoaderComplete={
                  activeIndex === index && autoplayEnabled
                    ? () => advanceRef.current()
                    : undefined
                }
              />
            ))}
            {autoplayEnabled && (
              <HeroSliderPlayPause
                isPlaying={isPlaying}
                onToggle={togglePlayPause}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

type Props = {
  slides: HeroSliderCarouselItemType[]
  config: HeroSliderConfigType | null | undefined
  layout?: 'Layout A' | 'Layout B' | null
}

/** Shared hero slides — carousel, autoplay, dots (TeaserHero Layout A / B). */
export default function HeroSlides({ slides, config, layout: layoutProp }: Props) {
  const prefersReducedMotion = usePrefersReducedMotion()
  if (slides.length === 0) return null

  const layout = layoutProp === 'Layout B' ? 'Layout B' : 'Layout A'
  const isCarousel = slides.length > 1

  if (!isCarousel) {
    return <StaticHeroSlide slide={slides[0]} layout={layout} />
  }

  return (
    <CarouselHeroSlides
      slides={slides}
      autoplayEnabled={config?.isAutoSlide === true && !prefersReducedMotion}
      intervalMs={slideIntervalMs(config?.slideInterval)}
      showControls={config?.sliderNavigationControls === true}
      layout={layout}
    />
  )
}
